import { inject, Injectable, OnDestroy } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { WoundsizeOutput } from '@modules/forms/form-modules/woundtrack/models/woundsize-output';
import { WoundsizeMeasureSource } from '@modules/shared/workers/woundsize-measure-source.enum';
import { WoundsizeWorker } from '@modules/shared/workers/woundsize-worker';
import { EventTopic } from '@modules/studio/event-bus/event-topic.enum';
import { SystemTool } from '@modules/studio/models/system-tool.enum';
import { UserTool } from '@modules/studio/models/user-tool.enum';
import { StudioEventService } from '@modules/studio/studio/studio-event.service';
import { StudioMetadataService } from '@modules/studio/studio/studio-metadata.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { approximate, Point, Polygon, Segment, simplifyPolygon, toPoints } from '@pixacare/geometry';
import { BehaviorSubject, first, map, startWith, switchMap, withLatestFrom } from 'rxjs';
import { WoundtrackOutputService } from './woundtrack-output.service';

const POINTS_COUNT_RATIO = 2500; // Ratio to calculate points count based on polygon perimeter and image resolution
const MAX_POINTS_COUNT = 100;
const MIN_POINTS_COUNT = 20;
const DEFAULT_WOUNDSIZE_OUTPUT: WoundsizeOutput = {
  shapes: null,
  result: null,
  items: [],
};

@UntilDestroy()
@Injectable()
export class WoundsizeService implements OnDestroy {
  private readonly studioEventService = inject(StudioEventService);
  private readonly studioMetadataService = inject(StudioMetadataService);
  private readonly outputService = inject(WoundtrackOutputService);
  private readonly transloco = inject(TranslocoService);

  private woundsizeWorker = new WoundsizeWorker();
  private readonly _isLoading$ = new BehaviorSubject<boolean>(true);
  private readonly _output$ = new BehaviorSubject<WoundsizeOutput>(DEFAULT_WOUNDSIZE_OUTPUT);

  readonly woundsizeShapes$ = this._output$.pipe(map(({ shapes }) => shapes));
  readonly woundsizeResult$ = this._output$.pipe(map(({ result }) => result));
  readonly woundsizeResultItems$ = this._output$.pipe(map(({ items }) => items));
  readonly isWoundsizeLoading$ = this._isLoading$.asObservable();

  readonly areWoundsizeButtonsDisabled$ = this.woundsizeShapes$.pipe(
    map((shapes) => !shapes),
    startWith(true),
  );

  readonly woundsizeDisabledHint$ = this.isWoundsizeLoading$.pipe(
    map((isLoading) => {
      if (isLoading) {
        return this.transloco.translate('Analyse en cours, veuillez patienter');
      }
      return this.transloco.translate('Veuillez compléter la forme avant de continuer');
    }),
  );

  constructor() {
    this.listenToEventService();
  }

  ngOnDestroy(): void {
    if (this.woundsizeWorker) {
      this.woundsizeWorker.terminate();
      this.woundsizeWorker = null;
    }
  }

  stopWoundsizeLoading(): void {
    this._isLoading$.next(false);
  }

  setWoundsizeWorker(stickerSurface: number): void {
    this.woundsizeWorker
      .get()
      .pipe(withLatestFrom(this.woundsizeResult$), untilDestroyed(this))
      .subscribe(([{ polygon, length, width, source }, previousResult]) => {
        const shapes = { polygon, length, width };
        const result = this.outputService.getWoundsizeResult(shapes, stickerSurface, source, previousResult);
        const resultItems = this.outputService.getWoundsizeResultItems(result);
        this.drawLengthWidth({ length, width });
        this.stopWoundsizeLoading();
        this._output$.next({ shapes, result, items: resultItems });
      });
  }

  drawPredictedWoundsize(polygon: Polygon): void {
    this.renderPolygon(polygon);
    this.measure(polygon.points, WoundsizeMeasureSource.AI_PREDICTION);
  }

  redrawWoundsize(): void {
    const { shapes } = this._output$.value;
    this.renderPolygon(shapes.polygon);
    this.drawLengthWidth(shapes);
  }

  downloadPicture(): void {
    this.studioEventService.publish(EventTopic.EXECUTE_TOOL, { tool: UserTool.DOWNLOAD });
  }

  private listenToEventService(): void {
    this.studioEventService
      .get(EventTopic.DELETE_SHAPE)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.onWoundsizeChanged([]);
        },
      });

    this.studioEventService
      .get(EventTopic.SHAPE_CLOSED)
      .pipe(
        map(({ points }) => toPoints(points)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (points) => {
          this.onWoundsizeChanged(points);
        },
      });

    this.studioEventService
      .get(EventTopic.SHAPE_MOVED)
      .pipe(
        map(({ points, index, point }) => ({ studioPoints: toPoints(points), index, point })),
        switchMap(({ studioPoints, index, point: [x, y] }) =>
          this.woundsizeShapes$.pipe(
            first(),
            map(({ polygon }) => approximate(polygon.points, studioPoints, { x, y }, index)),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe({
        next: (points) => {
          this.onWoundsizeChanged(points);
        },
      });
  }

  private onWoundsizeChanged(points: Point[]): void {
    if (!points || points.length < 3) {
      // A deleted shape clears the measure, but the predicted measure and whether it was measured manually remain.
      this._output$.next({
        ...DEFAULT_WOUNDSIZE_OUTPUT,
        result: this.outputService.clearWoundsizeMeasure(this._output$.value.result),
      });
      return;
    }

    this.measure(points, WoundsizeMeasureSource.USER_EDIT);
  }

  private measure(points: Point[], source: WoundsizeMeasureSource): void {
    this.woundsizeWorker.post({ points, source });
  }

  private renderPolygon({ points, edges }: Polygon): void {
    this.studioMetadataService.dimensions$.pipe(first()).subscribe((dimensions) => {
      const polygonPerimeter: number = edges.reduce((acc, edge) => acc + edge.length, 0);
      const imgResolution = dimensions.width * dimensions.height;
      const pointsCount = Math.max(
        Math.min(Math.sqrt(polygonPerimeter / imgResolution) * POINTS_COUNT_RATIO, MAX_POINTS_COUNT),
        Math.min(points.length - 1, MIN_POINTS_COUNT),
      );
      this.studioEventService.publish(EventTopic.IMPORT_POINTS, {
        points: simplifyPolygon(points, pointsCount).flatMap(({ x, y }) => [x, y]),
      });
    });
  }

  private drawLengthWidth({ length, width }: { length: Segment; width: Segment }): void {
    this.studioEventService.publish(EventTopic.EXECUTE_TOOL, {
      tool: SystemTool.DRAW_LENGTH_WIDTH,
      args: {
        length,
        width,
      },
    });
  }
}
